import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Iconify from './Iconify';
import { CircularProgress, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { UploadAssetsApiCall } from '../utils/uploadAssetsApiCall';
import { useAppSelector } from '../store';
import { getItem } from '../utils/localStorage';
import { config } from '../config';
import { removeUploadAssets } from '../utils/removeUploadAssetsApiCall';
import { toast } from 'react-toastify';
import { TfiReload } from 'react-icons/tfi';
import heic2any from 'heic2any';
import Compressor from 'compressorjs';

const baseStyle = {
  display: 'flex',
  FlexDirection: 'column',
  alignItems: 'center',
  padding: '20px',
  borderWidth: 2,
  borderRadius: 2,
  borderColor: '#eeeeee',
  borderStyle: 'dashed',
  backgroundColor: '#fafafa',
  color: '#bdbdbd',
  transition: 'border .3s ease-in-out',
  height: '110px',
  justifyContent: 'center',
  marginBottom: '40px',
};

const activeStyle = {
  borderColor: '#2196f3',
};

const acceptStyle = {
  borderColor: '#00e676',
};

const rejectStyle = {
  borderColor: '#ff1744',
};

const boxImgSX = {
  position: 'absolute',
  top: '3px',
  right: '5px',
  padding: '2px',

  backgroundColor: 'rgba(22, 28, 36, 0.72)',
  '&:hover': {
    color: '#000',
    backgroundColor: 'rgb(0 0 0)',
  },
  '@media(max-width: 600px)': {
    top: '4px',
    right: '2px',
    position: 'absolute',
    padding: '2px',
  },
  '@media(max-width: 500px)': {
    top: '2px',
    right: '1px',
    position: 'absolute',
    padding: '2px',
  },
};

const boxImgStaticSX = {
  position: 'absolute',
  top: '4px',
  right: '12px',
  padding: '2px',
  backgroundColor: 'rgba(22, 28, 36, 0.72)',
  '&:hover': {
    color: '#000',
    backgroundColor: 'rgb(0 0 0)',
  },
  '@media(max-width: 600px)': {
    top: '4px',
    right: '2px',
    position: 'absolute',
    padding: '2px',
  },
  '@media(max-width: 500px)': {
    top: '2px',
    right: '1px',
    position: 'absolute',
    padding: '2px',
  },
};

const boxImgPdfStaticSX = {
  position: 'absolute',
  top: '10px',
  right: '14px',
  padding: '2px',
  backgroundColor: 'rgba(22, 28, 36, 0.72)',
  '&:hover': {
    color: '#000',
    backgroundColor: 'rgb(0 0 0)',
  },
  '@media(max-width: 600px)': {
    top: '10px',
    right: '10px',
    position: 'absolute',
    padding: '2px',
  },
  '@media(max-width: 500px)': {
    top: '4px',
    right: '3px',
    position: 'absolute',
    padding: '2px',
  },
};

const boxReTrySX = {
  position: 'absolute',
  top: '18%',
  right: '18%',
  backgroundColor: 'rgba(22, 28, 36, 0.72)',
  '&:hover': {
    color: '#000',
    backgroundColor: 'rgb(0 0 0)',
  },
  '@media(max-width: 600px)': {
    top: '30%',
    right: '15%',
    position: 'absolute',
    padding: '2px',
  },
  '@media(max-width: 500px)': {
    top: '30%',
    right: '15%',
    position: 'absolute',
    padding: '2px',
  },
};

interface File {
  path: string;
  name: string;
  preview: string;
  type: string;
  loader?: boolean;
  id?: string;
  key?: string;
  mimeType?: string;
  retry?: boolean;
}
interface IProps {
  setFiles: React.Dispatch<React.SetStateAction<File[]>>;
  files: File[];
  module: string;
  moduleId?: string;
  onClose: () => void;
  setPreviewAttachments?: React.Dispatch<React.SetStateAction<any>>;
}

const getFileMimeType = (file: File) => file?.type || '';

const patchFileState = (
  file: File,
  patch: Partial<File>
): File => Object.assign(file, patch);

const cloneFilesForRender = (fileList: File[]) => [...fileList];

const isFileUploading = (file: File) =>
  Boolean(file.loader) || (!file.id && !file.retry);

const getFilePreview = async (file: any) => {
  if (
    file.type == 'image/heic' ||
    file.type == 'image/heif' ||
    file.type == 'application/octet-stream'
  ) {
    const blobURL = URL.createObjectURL(file);
    const res = await fetch(blobURL);
    const blob = await res.blob();
    return URL.createObjectURL((await heic2any({ blob })) as Blob);
  } else {
    return URL.createObjectURL(file);
  }
};

const DropzoneComponent: React.FC<IProps> = ({
  files,
  setFiles,
  module,
  moduleId,
  setPreviewAttachments,
}) => {
  const organizationId = getItem(config.ORGANIZATION_ID);
  const { accessToken } = useAppSelector((state) => state.auth);
  const inFlightUploads = useRef(new WeakSet<object>());
  const [openLightBox, setOpenLightBox] = useState(false);
  const [activeLightBoxIndex, setActiveLightBoxIndex] = useState(0);
  const [rotation, setRotation] = useState(0);

  const imageFiles = useMemo(
    () =>
      files.filter((file) => getFileMimeType(file).split('/')[0] === 'image'),
    [files]
  );

  const handleOpenLightBox = (file: File) => {
    const idx = imageFiles.findIndex((item) => item === file);
    if (idx < 0) return;
    setActiveLightBoxIndex(idx);
    setOpenLightBox(true);
  };

  const handleCloseLightBox = () => {
    setRotation(0);
    setOpenLightBox(false);
    setActiveLightBoxIndex(0);
  };

  const handleNextImage = () => {
    setRotation(0);
    if (activeLightBoxIndex >= imageFiles.length - 1) return;
    setActiveLightBoxIndex((curr) => curr + 1);
  };

  const handlePrvImage = () => {
    setRotation(0);
    if (activeLightBoxIndex === 0) return;
    setActiveLightBoxIndex((curr) => curr - 1);
  };

  const handleRotate = () => {
    setRotation((curr) => curr + 90);
  };

  const refreshFiles = useCallback(() => {
    setFiles((current) => cloneFilesForRender(current));
  }, [setFiles]);

  const uploadSingleFile = useCallback(
    async (file: File) => {
      if (file.id || inFlightUploads.current.has(file)) {
        return;
      }

      inFlightUploads.current.add(file);
      patchFileState(file, { loader: true, retry: false });
      refreshFiles();

      try {
        const result = await UploadAssetsApiCall(
          file,
          organizationId,
          accessToken,
          module,
          moduleId
        );

        if (result?.id) {
          patchFileState(file, {
            id: result.id,
            key: result.key,
            mimeType: result.mimeType || file.type,
            loader: false,
            retry: false,
          });
        } else {
          patchFileState(file, { loader: false, retry: true });
        }
      } finally {
        inFlightUploads.current.delete(file);
        refreshFiles();
      }
    },
    [accessToken, module, moduleId, organizationId, refreshFiles]
  );

  const compressImages = (filesToCompress: any[]) => {
    const promiseList: Promise<any>[] = [];
    for (const file of filesToCompress) {
      // Only compress images. Skip PDFs and other documents
      if (file.type?.split('/')[0] === 'image' && file.type !== 'image/gif') {
        promiseList.push(
          new Promise((resolve) => {
            new Compressor(file, {
              quality: 0.4,
              success(compressedFile) {
                resolve(compressedFile);
              },
              error(_) {
                resolve(file);
              },
            });
          })
        );
      } else {
        // For PDFs, documents, and other non-image files, use as-is
        promiseList.push(Promise.resolve(file));
      }
    }
    return Promise.all(promiseList);
  };

  const onDrop = useCallback(
    async (acceptedFiles: any) => {
      const acceptedFileResult: File[] = [];
      const compressedFileList = await compressImages(acceptedFiles);

      for await (const file of compressedFileList) {
        acceptedFileResult.push(
          Object.assign(file, {
            preview: await getFilePreview(file),
            loader: true,
            retry: false,
          })
        );
      }

      setFiles((curr) => [...curr, ...acceptedFileResult]);

      // Upload every newly dropped file in parallel. Avoid a files useEffect —
      // that re-ran on each setFiles and let Confirm enable after the first id.
      await Promise.all(
        acceptedFileResult.map((file) => uploadSingleFile(file))
      );
    },
    [setFiles, uploadSingleFile]
  );

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    onDrop,
    // Accept images, PDFs, and common document types for broker license and ID uploads
    accept: {
      'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.heic', '.heif', '.bmp', '.tiff'],
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    },
    // Allow multiple file types but single upload mode will be enforced by the modal
  });

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isDragActive ? activeStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isDragActive, isDragReject, isDragAccept]
  );

  const thumbs = files.map((file: File, index: number) => {
    const mimeType = getFileMimeType(file);
    const mimeCategory = mimeType.split('/')[0];
    const mimeSubtype = mimeType.split('/')[1];
    const uploading = isFileUploading(file);
    const showRetry = Boolean(file.retry) && !file.loader;
    const showRemove = Boolean(file.id) && !file.loader && !file.retry;

    return (
      <Grid
        container
        xs={3}
        sm={2}
        md={2}
        key={`${file.name}-${file.id || file.preview || index}`}
        sx={{
          paddingLeft: '24px',
          position: 'relative',
          marginTop: '15px',
          '@media (max-width: 500px)': {
            paddingLeft: '8px',
          },
        }}
        justifyContent='space-between'
        alignItems='center'
      >
        {mimeCategory === 'image' ? (
          <>
            <img
              src={file.preview}
              alt={file.name}
              onClick={() => {
                if (!uploading) {
                  handleOpenLightBox(file);
                }
              }}
              style={{
                height: '100%',
                width: '100%',
                borderRadius: '8px',
                cursor: uploading ? 'default' : 'pointer',
                objectFit: 'cover',
              }}
            />
            {uploading && (
              <Box sx={{ display: 'flex' }}>
                <CircularProgress
                  size={25}
                  sx={{
                    position: 'absolute',
                    top: '35%',
                    right: '25%',
                  }}
                />
              </Box>
            )}
            {showRetry && (
              <IconButton
                component='label'
                sx={boxReTrySX}
                onClick={(e) => {
                  e.stopPropagation();
                  retryFunction(file);
                }}
              >
                <TfiReload
                  style={{ height: '18px', width: '18px', color: '#fff' }}
                />
              </IconButton>
            )}
            {showRemove && (
              <IconButton
                component='label'
                onClick={(e) => {
                  e.stopPropagation();
                  removeFile(file);
                }}
                sx={boxImgSX}
              >
                <Iconify
                  icon={'eva:close-outline'}
                  width={18}
                  height={18}
                  color='#fff'
                />
              </IconButton>
            )}
          </>
        ) : mimeSubtype === 'pdf' ? (
          <>
            <img
              src='/static/pdf.png'
              style={{ height: '100%', width: '100%', borderRadius: '8px' }}
            />
            {uploading && (
              <Box sx={{ display: 'flex' }}>
                <CircularProgress
                  size={25}
                  sx={{
                    position: 'absolute',
                    top: '35%',
                    right: '25%',
                  }}
                />
              </Box>
            )}
            {showRetry && (
              <IconButton
                component='label'
                sx={boxReTrySX}
                onClick={() => retryFunction(file)}
              >
                <TfiReload
                  style={{ height: '18px', width: '18px', color: '#fff' }}
                />
              </IconButton>
            )}
            {showRemove && (
              <IconButton
                component='label'
                onClick={() => removeFile(file)}
                sx={boxImgPdfStaticSX}
              >
                <Iconify
                  icon={'eva:close-outline'}
                  width={18}
                  height={18}
                  color='#fff'
                />
              </IconButton>
            )}
          </>
        ) : mimeCategory === 'video' ? (
          <>
            <img
              src='/static/video_placeholder.png'
              style={{ height: '100%', width: '100%', borderRadius: '8px' }}
            />
            {uploading && (
              <Box sx={{ display: 'flex' }}>
                <CircularProgress
                  size={25}
                  sx={{
                    position: 'absolute',
                    top: '35%',
                    right: '25%',
                  }}
                />
              </Box>
            )}
            {showRetry && (
              <IconButton
                component='label'
                sx={boxReTrySX}
                onClick={() => retryFunction(file)}
              >
                <TfiReload
                  style={{ height: '18px', width: '18px', color: '#fff' }}
                />
              </IconButton>
            )}
            {showRemove && (
              <IconButton
                component='label'
                onClick={() => removeFile(file)}
                sx={boxImgPdfStaticSX}
              >
                <Iconify
                  icon={'eva:close-outline'}
                  width={18}
                  height={18}
                  color='#fff'
                />
              </IconButton>
            )}
          </>
        ) : (
          <>
            <img
              src='/static/docket.png'
              style={{ height: '100%', width: '100%', borderRadius: '8px' }}
            />
            {uploading && (
              <Box sx={{ display: 'flex' }}>
                <CircularProgress
                  size={25}
                  sx={{
                    position: 'absolute',
                    top: '35%',
                    right: '25%',
                  }}
                />
              </Box>
            )}
            {showRetry && (
              <IconButton
                component='label'
                sx={boxReTrySX}
                onClick={() => retryFunction(file)}
              >
                <TfiReload
                  style={{ height: '18px', width: '18px', color: '#fff' }}
                />
              </IconButton>
            )}
            {showRemove && (
              <IconButton
                component='label'
                onClick={() => removeFile(file)}
                sx={boxImgStaticSX}
              >
                <Iconify
                  icon={'eva:close-outline'}
                  width={18}
                  height={18}
                  color='#fff'
                />
              </IconButton>
            )}
          </>
        )}
      </Grid>
    );
  });

  useEffect(() => {
    if (setPreviewAttachments) {
      setPreviewAttachments(files);
    }
  }, [files, setPreviewAttachments]);

  const retryFunction = async (file: File) => {
    patchFileState(file, { id: undefined, retry: false });
    await uploadSingleFile(file);
  };

  const removeFile = async (file: File) => {
    const result = await removeUploadAssets(
      accessToken,
      file.id,
      organizationId
    );
    if (result === true) {
      if (file?.preview && file.preview.startsWith('blob:')) {
        URL.revokeObjectURL(file.preview);
      }
      setFiles((current) => current.filter((item) => file.id !== item.id));
    } else {
      toast.error('Something went wrong');
    }
  };

  // Only revoke blob: preview URLs for files that were removed — not on every
  // refreshFiles() during multi-upload (that broke 2nd/3rd image previews).
  const previousFilesRef = useRef<File[]>([]);
  useEffect(() => {
    const previousFiles = previousFilesRef.current;
    previousFiles.forEach((previousFile) => {
      const stillPresent = files.some((file) => file === previousFile);
      if (
        !stillPresent &&
        previousFile?.preview &&
        previousFile.preview.startsWith('blob:')
      ) {
        URL.revokeObjectURL(previousFile.preview);
      }
    });
    previousFilesRef.current = files;
  }, [files]);

  return (
    <Box sx={{ py: 2 }}>
      <section>
        <div {...getRootProps({ style })}>
          <input {...getInputProps()} />
          <div>Drop file or Click to browse</div>
        </div>
        <Box sx={{ flexGrow: 1 }}>
          <Grid
            container
            spacing={{ xs: 1, md: 1 }}
            columns={{ xs: 4, sm: 8, md: 12 }}
          >
            {thumbs}
          </Grid>
        </Box>
      </section>

      {openLightBox && imageFiles[activeLightBoxIndex] && (
        <Box
          sx={{ pt: 'env(safe-area-inset-top) !important' }}
          id='uploadPreviewModal'
          className='modal'
          onClick={handleCloseLightBox}
        >
          <span className='close cursor'>
            <CloseIcon
              fontSize='large'
              onClick={handleCloseLightBox}
              sx={{ mt: 'env(safe-area-inset-top) !important' }}
            />
          </span>
          <div
            className='modal-content'
            onClick={(e) => e.stopPropagation()}
          >
            <div className='vertical-center-div'>
              <div className='mySlides'>
                <img
                  src={imageFiles[activeLightBoxIndex].preview}
                  alt={imageFiles[activeLightBoxIndex].name}
                  style={{
                    transform: `rotate(${rotation}deg)`,
                    maxWidth: 'min(100%, 720px)',
                    maxHeight: 'min(80vh, 520px)',
                    width: 'auto',
                    height: 'auto',
                    objectFit: 'contain',
                  }}
                />
              </div>
            </div>
            {imageFiles.length > 1 && (
              <>
                <div
                  className='prev'
                  onClick={handlePrvImage}
                >
                  <i className='fa-solid fa-arrow-left'></i>
                </div>
                <div
                  className='next'
                  onClick={handleNextImage}
                >
                  <i className='fa-solid fa-arrow-right'></i>
                </div>
              </>
            )}
            <div
              className='rotate'
              onClick={handleRotate}
            >
              <i className='fa-solid fa-camera-rotate'></i>
            </div>
          </div>
        </Box>
      )}
    </Box>
  );
};

export default DropzoneComponent;
